import React, { useState } from 'react'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { HStack } from '@astryxdesign/core/HStack'
import PillButton from './PillButton'
import { IconButton } from '@astryxdesign/core/IconButton'
import { ChevronDown, ChevronRight, Copy } from 'lucide-react'

interface DebugModalProps {
  conversationId: string
  entries: AgentDebugEntry[]
  onClose: () => void
}

function DebugEntryCard({
  entry,
  index,
  defaultExpanded
}: {
  entry: AgentDebugEntry
  index: number
  defaultExpanded: boolean
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded)
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null)

  const handleCopy = (text: string, messageIndex: number): void => {
    void navigator.clipboard.writeText(text)
    setCopiedMessageIndex(messageIndex)
    setTimeout(
      () => setCopiedMessageIndex((current) => (current === messageIndex ? null : current)),
      1200
    )
  }

  return (
    <div className="debug-modal-entry">
      <button
        type="button"
        className="debug-modal-entry-header"
        onClick={() => setExpanded((value) => !value)}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="debug-modal-entry-label">
          {index + 1}. {entry.label}
        </span>
        <span className="debug-modal-entry-model">{entry.model}</span>
        <span className="debug-modal-entry-tokens">
          {entry.inputTokens.toLocaleString()} in / {entry.outputTokens.toLocaleString()} out
        </span>
      </button>
      {expanded && (
        <div className="debug-modal-entry-body">
          {entry.messages.map((message, messageIndex) => (
            <div key={messageIndex} className="debug-modal-message">
              <div className="debug-modal-message-header">
                <span className="debug-modal-message-role">{message.role}</span>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Copy size={11} />}
                  label="Copy message"
                  tooltip={copiedMessageIndex === messageIndex ? 'Copied' : 'Copy message'}
                  onClick={() => handleCopy(message.content, messageIndex)}
                />
              </div>
              <pre className="debug-modal-message-content">{message.content}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function DebugModal({
  conversationId,
  entries,
  onClose
}: DebugModalProps): React.JSX.Element {
  const totalInputTokens = entries.reduce((sum, entry) => sum + entry.inputTokens, 0)
  const totalOutputTokens = entries.reduce((sum, entry) => sum + entry.outputTokens, 0)
  const modelsUsed = Array.from(new Set(entries.map((entry) => entry.model)))
  const [copiedId, setCopiedId] = useState(false)

  const handleCopyConversationId = (): void => {
    void navigator.clipboard.writeText(conversationId)
    setCopiedId(true)
    setTimeout(() => setCopiedId(false), 1200)
  }

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => !open && onClose()}
      variant="standard"
      purpose="form"
      width="min(90vw, 1100px)"
      maxHeight="85vh"
    >
      <DialogHeader
        title="Debug info"
        subtitle={`${entries.length} model call${entries.length === 1 ? '' : 's'} — ${totalInputTokens.toLocaleString()} input / ${totalOutputTokens.toLocaleString()} output tokens\nModel${modelsUsed.length === 1 ? '' : 's'}: ${modelsUsed.join(', ')}`}
        onOpenChange={() => onClose()}
        style={{ whiteSpace: 'pre-line' }}
      />
      <div className="debug-modal-scroll">
        <div className="debug-modal-container">
          {/* DialogHeader's subtitle is a plain string, so the id lives here where it can be a
              button. The whole line is the click target: nothing to aim for, click anywhere. */}
          <button
            type="button"
            className="debug-modal-conversation-id"
            title={copiedId ? 'Copied' : 'Click to copy conversation id'}
            aria-label={copiedId ? 'Copied conversation id' : 'Copy conversation id'}
            onClick={handleCopyConversationId}
          >
            <span className="debug-modal-conversation-id-label">Conversation ID</span>
            <code>{conversationId}</code>
            <span className="debug-modal-conversation-id-hint">
              {copiedId ? 'Copied' : <Copy size={11} />}
            </span>
          </button>
          {entries.map((entry, index) => (
            <DebugEntryCard key={index} entry={entry} index={index} defaultExpanded={index === 0} />
          ))}
        </div>
      </div>
      <div className="debug-modal-footer">
        <HStack gap={1} justify="end">
          <PillButton variant="ghost" label="Close" onClick={onClose} />
        </HStack>
      </div>
    </Dialog>
  )
}
